import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  useFetchCustomerOrderDetails,
  useFetchUser,
  useSelectedOrderDetails,
} from "store/backend/assets";
import { ErrorMessage, PrimaryButton } from "widgets";
import Loader from "Shared/Loader/Loader";
import CustomerOrderDetails from "components/CustomerOrderDetails/CustomerOrderDetails";
import CustomerDetailsHeader from "components/CustomerDetailsHeader/CustomerDetailsHeader";
import ErrorFallback from "components/ErrorFallBack/ErrorFallback";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";
import PageNotFound from "components/PageNotFound/PageNotFound";

import styles from "./CustomerOrder.module.scss";

const handleOnClickPunchOrderButton = (
  navigate,
  uamId,
  selectedBusinessVertical
) => {
  return navigate(
    `/punchOrder?uamId=${uamId}&verticalId=${selectedBusinessVertical}`
  );
};

const CustomerOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uamId = searchParams.get("uamId");
  const selectedBusinessVerticalId = searchParams.get("verticalId");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderID, setSelectedOrderID] = useState(null);

  const isInvalidParam =
    !uamId ||
    !selectedBusinessVerticalId ||
    uamId === "null" ||
    uamId === "undefined" ||
    selectedBusinessVerticalId === "null" ||
    selectedBusinessVerticalId === "undefined";

  const {
    isPending: isSelectedSelectedUserDetailsLoading,
    data: selectedUserDetails,
    isError: isErrorSelectedSelectedUserDetails,
    error: errorMessageOnSelectedSelectedUserDetails,
  } = useFetchUser(uamId, selectedBusinessVerticalId);

  const {
    isPending: isOrderDetailsLoading,
    data: orderDetails,
    isError: isErrorOnCustomerOrderDetails,
    error: errorMessageOnCustomerOrderDetails,
  } = useFetchCustomerOrderDetails({ uamId }, { enabled: !isInvalidParam });

  const {
    isPending: isSelectedOrderDetailsLoading,
    data: selectedOrderDetails,
    isError: isErrorOnSelectedOrderDetails,
    error: errorMessageOnSelectedOrderDetails,
  } = useSelectedOrderDetails(selectedOrderID, { enabled: !isInvalidParam });

  useEffect(() => {
    if (Array.isArray(orderDetails) && orderDetails.length > 0) {
      setSelectedOrderID(orderDetails[0].order_id);
    } else if (Array.isArray(orderDetails) && orderDetails.length === 0) {
      setSelectedOrderID(null);
    }
  }, [orderDetails]);

  useEffect(() => {
    if (
      selectedOrderID &&
      !isSelectedOrderDetailsLoading &&
      !isErrorOnSelectedOrderDetails
    ) {
      setSelectedOrder(selectedOrderDetails);
    }
  }, [selectedOrderID, isSelectedOrderDetailsLoading, selectedOrderDetails]);

  if (isInvalidParam) {
    return <PageNotFound />;
  }

  const isSelectedOrderLoading = !!selectedOrderID
    ? isSelectedOrderDetailsLoading
      ? true
      : false
    : false;

  if (isInvalidParam) {
    return <PageNotFound />;
  }
  if (
    isOrderDetailsLoading ||
    isSelectedOrderLoading ||
    isSelectedSelectedUserDetailsLoading
  ) {
    return <FullPageLoader />;
  }

  if (isErrorOnSelectedOrderDetails) {
    return (
      <ErrorFallback message={errorMessageOnSelectedOrderDetails.message} />
    );
  }

  if (isErrorSelectedSelectedUserDetails) {
    return (
      <ErrorFallback
        message={errorMessageOnSelectedSelectedUserDetails.message}
      />
    );
  }

  if (!uamId) {
    return <PageNotFound />;
  }

  const orderStatus = selectedOrder?.status?.id || null;
  const selectedBusinessVertical = selectedOrder?.business_unit_id || null;

  return (
    <div className={styles.mainContainer}>
      {isOrderDetailsLoading ? (
        <Loader />
      ) : (
        <>
          {uamId !== null && uamId !== undefined && isOrderDetailsLoading ? (
            <Loader />
          ) : isErrorOnCustomerOrderDetails ? (
            <ErrorMessage>
              {errorMessageOnCustomerOrderDetails?.message}
            </ErrorMessage>
          ) : Array.isArray(orderDetails) && orderDetails.length > 0 ? (
            <div className={styles.customerDetailsAndPackageDetailsContainer}>
              <CustomerDetailsHeader
                selectedBusinessVertical={selectedBusinessVertical}
                uamId={uamId}
                orderDetails={orderDetails}
              />
              <CustomerOrderDetails
                orderId={selectedOrderID}
                orderDetails={orderDetails}
                selectedOrder={selectedOrder}
                setSelectedOrderID={setSelectedOrderID}
                setSelectedOrder={setSelectedOrder}
                orderStatus={orderStatus}
                uamId={uamId}
              />
            </div>
          ) : (
            uamId !== null &&
            Array.isArray(orderDetails) &&
            orderDetails.length === 0 && (
              <div className={styles.punchOrderButtonContainer}>
                <div className={styles.noOrdersFoundText}>No orders found!</div>
                <div>Please punch a new order</div>
                <PrimaryButton
                  onClick={() =>
                    handleOnClickPunchOrderButton(
                      navigate,
                      uamId,
                      selectedBusinessVerticalId
                    )
                  }
                >
                  Punch Order
                </PrimaryButton>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default CustomerOrder;
